import React, { useEffect, useState, useCallback } from "react";
import { Navbar, MobileNav } from "@/components/Navbar";
import { OpportunityCard } from "@/components/OpportunityCard";
import { LocationSelect } from "@/components/LocationSelect";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Search, SlidersHorizontal, Loader2, Megaphone, Ticket } from "lucide-react";

const empty = { q: "", location: "all", niche: "all", platform: "all", compensation: "all", type: "all", sort: "relevant", min_match: "all" };
const TYPE_SEGMENTS = [
  { v: "all", l: "All" }, { v: "collaboration", l: "Collaborations" },
  { v: "paid", l: "Paid" }, { v: "gifted", l: "Gifted" }, { v: "event", l: "Events" },
];

function Filters({ f, setF, meta }) {
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="space-y-5">
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Location</Label>
        <LocationSelect testid="opp-filter-location" value={f.location} onChange={(v) => set("location", v)}
          groups={meta.location_groups || []} leading={[{ value: "all", label: "All locations" }]} />
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Niche</Label>
        <Select value={f.niche} onValueChange={(v) => set("niche", v)}>
          <SelectTrigger data-testid="opp-filter-niche" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All niches</SelectItem>{meta.niches?.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Platform</Label>
        <Select value={f.platform} onValueChange={(v) => set("platform", v)}>
          <SelectTrigger data-testid="opp-filter-platform" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any platform</SelectItem><SelectItem value="instagram">Instagram</SelectItem><SelectItem value="tiktok">TikTok</SelectItem><SelectItem value="youtube">YouTube</SelectItem></SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Compensation</Label>
        <Select value={f.compensation} onValueChange={(v) => set("compensation", v)}>
          <SelectTrigger data-testid="opp-filter-comp" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="gifted">Gifted</SelectItem><SelectItem value="paid_gifted">Paid + Gifted</SelectItem></SelectContent>
        </Select>
      </div>
      <Button variant="outline" data-testid="opp-filter-reset" onClick={() => setF(empty)} className="w-full">Reset</Button>
    </div>
  );
}

export default function Opportunities() {
  const [meta, setMeta] = useState({});
  const [f, setF] = useState(empty);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nearby, setNearby] = useState([]);
  const { user } = useAuth();

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => { api.get("/events/nearby").then((r) => setNearby(r.data.events || [])).catch(() => {}); }, []);

  const fetchOpps = useCallback(async () => {
    setLoading(true);
    const params = { sort: f.sort };
    ["location", "niche", "platform", "compensation"].forEach((k) => { if (f[k] !== "all") params[k] = f[k]; });
    if (f.type && f.type !== "all") params.type = f.type;
    if (f.q) params.q = f.q;
    if (f.min_match && f.min_match !== "all") params.min_match = Number(f.min_match);
    try { const r = await api.get("/opportunities", { params }); setOpps(r.data.opportunities); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { const t = setTimeout(fetchOpps, 300); return () => clearTimeout(t); }, [fetchOpps]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-zinc-900"><Megaphone className="h-7 w-7 text-emerald-600" /> Opportunities</h1>
          <p className="mt-1 text-zinc-500">Local collaborations you can apply for — ranked for you.</p>
        </div>

        {user?.role !== "business" && nearby.length > 0 && (
          <div data-testid="nearby-events" className="mb-6 rounded-2xl border border-fuchsia-200 bg-fuchsia-50/50 p-4">
            <div className="mb-3 flex items-center gap-2">
              <Ticket className="h-5 w-5 text-fuchsia-600" />
              <h2 className="font-display font-bold text-zinc-900">Events near you this week</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {nearby.map((o, i) => (
                <div key={o.id} className="w-72 shrink-0"><OpportunityCard opp={o} index={i} /></div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-2" data-testid="opp-type-segments">
          {TYPE_SEGMENTS.map((s) => (
            <button key={s.v} data-testid={`opp-type-${s.v}`} onClick={() => setF({ ...f, type: s.v })}
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${f.type === s.v ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"}`}>{s.l}</button>
          ))}
        </div>

        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="opp-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search opportunities…" className="h-11 pl-9" />
          </div>
          <Select value={f.sort} onValueChange={(v) => setF({ ...f, sort: v })}>
            <SelectTrigger data-testid="opp-sort" className="h-11 sm:w-48"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="match">Best match</SelectItem><SelectItem value="relevant">Recommended</SelectItem><SelectItem value="nearest">Nearest</SelectItem><SelectItem value="newest">Newest</SelectItem><SelectItem value="deadline">Deadline soon</SelectItem></SelectContent>
          </Select>
          {user?.role === "creator" && (
            <Select value={f.min_match} onValueChange={(v) => setF({ ...f, min_match: v })}>
              <SelectTrigger data-testid="opp-match-filter" className="h-11 sm:w-44"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All matches</SelectItem><SelectItem value="70">Good match & up (70+)</SelectItem><SelectItem value="85">Strong match only (85+)</SelectItem></SelectContent>
            </Select>
          )}
          <Sheet>
            <SheetTrigger asChild><Button variant="outline" data-testid="opp-mobile-filter" className="h-11 lg:hidden"><SlidersHorizontal className="mr-2 h-4 w-4" /> Filters</Button></SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto"><SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader><div className="mt-6"><Filters f={f} setF={setF} meta={meta} /></div></SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-8">
          <aside className="hidden w-64 shrink-0 lg:block"><div className="sticky top-24 rounded-2xl border border-zinc-200 bg-white p-5"><Filters f={f} setF={setF} meta={meta} /></div></aside>
          <div className="flex-1">
            {loading ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
              : opps.length === 0 ? (
                <div data-testid="opp-empty" className="flex h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 text-center">
                  <p className="font-display text-lg font-semibold text-zinc-700">No opportunities yet</p>
                  <p className="mt-1 text-sm text-zinc-500">Check back soon — businesses post new ones regularly.</p>
                </div>
              ) : (
                <>
                  <p className="mb-4 text-sm text-zinc-500">{opps.length} opportunit{opps.length !== 1 ? "ies" : "y"}</p>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">{opps.map((o, i) => <OpportunityCard key={o.id} opp={o} index={i} />)}</div>
                </>
              )}
          </div>
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
